import type {
    CreateVehicleTO,
    UpdateVehicleTO,
    VehicleTO,
} from '@control-center/control-center-client-public-vehicles-ts';
import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { vehicleFromRestV2, vehicleSummaryFromRestV2, vehiclesFromRestV2 } from 'domain/mappers/vehicleFromRestV2';
import type { VehicleFE, VehicleSummaryFE } from 'domain/vehicle';
import { client } from 'graphql/client';
import { getVehicleByVINV2, listVehiclesByFleetIdV2 } from 'graphql/queries';
import { onUpdateVehicleByVINV2, onUpdateVehicleV2 } from 'graphql/subscriptions';
import { SafeVehicleV2ListSchema, SafeVehicleV2Schema } from 'helper/v2Schemas';
import { v4 as uuid } from 'uuid';

import { errorHandler, formatAxiosError, isAxiosError } from './errors';
import { applyVehicleLiveUpdate } from './mappers/vehicleLiveFieldsFromV2';
import { correlationHeaders, vehiclesApi } from './restClient';
import { denormalizeListWithVin, fetchV2, type GenericRecord } from './utils';

import type {
    DeleteVehicleDeviationsInput,
    GetVehicleByVINV2Query,
    ListVehiclesByFleetIdV2Query,
    OnUpdateVehicleV2SubscriptionVariables,
    VehicleList,
    VehicleV2,
} from 'API';

const extractVehicle = (result: GetVehicleByVINV2Query['getVehicleByVINV2']): VehicleV2 => {
    const errorResult = result as { __typename?: string; errorCode?: string } | null;
    if (!errorResult || errorResult.__typename === 'ErrorResponse' || errorResult.errorCode !== undefined) {
        throw new Error(errorResult?.errorCode ?? 'Vehicle not found');
    }
    return result as VehicleV2;
};

const extractVehicleList = (result: ListVehiclesByFleetIdV2Query['listVehiclesByFleetIdV2']): VehicleV2[] => {
    const errorResult = result as { __typename?: string; errorCode?: string } | null;
    if (!errorResult || errorResult.__typename === 'ErrorResponse' || errorResult.errorCode !== undefined) {
        throw new Error(errorResult?.errorCode ?? 'Vehicle list not found');
    }
    return ((result as VehicleList).items ?? []).filter((v): v is VehicleV2 => v !== null);
};

const listVehiclesSummaryQueryFn = async (): Promise<{ data: VehicleSummaryFE[] }> => {
    try {
        const { data } = await vehiclesApi.listVehicleSummaries();
        return { data: data.map(vehicleSummaryFromRestV2) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('Unexpected error occurred while fetching the V2 vehicles summary', loggableError);
    }
};

const parseVehicle = (data: VehicleV2) => SafeVehicleV2Schema().parse(data);

const parseVehicleList = (list: VehicleV2[]) => denormalizeListWithVin(SafeVehicleV2ListSchema().parse(list));

const listVehiclesV2QueryFn = async (): Promise<{ data: GenericRecord<VehicleFE> }> => {
    try {
        const { data } = await vehiclesApi.listVehicles(correlationHeaders());
        return { data: denormalizeListWithVin(vehiclesFromRestV2(data)) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('Unexpected error occurred while fetching the V2 vehicles', loggableError);
    }
};

const listVehiclesByFleetIdV2QueryFn = (fleetId: string) =>
    fetchV2(
        () => client.graphql({ query: listVehiclesByFleetIdV2, variables: { fleetId } }),
        (data: ListVehiclesByFleetIdV2Query) => parseVehicleList(extractVehicleList(data.listVehiclesByFleetIdV2)),
        'Unexpected error occurred while fetching the V2 vehicles by fleet'
    );

const getVehicleByVINV2QueryFn = (vin: string) =>
    fetchV2(
        () => client.graphql({ query: getVehicleByVINV2, variables: { vin } }),
        (data: GetVehicleByVINV2Query) => parseVehicle(extractVehicle(data.getVehicleByVINV2)),
        'Unexpected error occurred while fetching the V2 vehicle info'
    );

const getVehicleByVinQueryFn = async (vin: string): Promise<{ data: VehicleFE }> => {
    try {
        const { data } = await vehiclesApi.getVehicleByVin(vin, { headers: { 'x-correlation-id': uuid() } });
        return { data: vehicleFromRestV2(data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            `Unexpected error occurred while fetching the REST vehicle info for vin: ${vin}`,
            loggableError
        );
    }
};

type CreateVehicleQueryResult = { data: VehicleTO } | { error: { status: number; data: string } };

const createVehicleQueryFn = async (vehicle: CreateVehicleTO): Promise<CreateVehicleQueryResult> => {
    try {
        const { data } = await vehiclesApi.createVehicle(vehicle, { headers: { 'x-correlation-id': uuid() } });
        return { data };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        const handledError = errorHandler('Unexpected error occurred while creating the vehicle', loggableError);

        if (isAxiosError(error) && error.response?.status) {
            return {
                error: {
                    status: error.response.status,
                    data: handledError.message,
                },
            };
        }

        throw handledError;
    }
};

type UpdateVehicleQueryResult = { data: VehicleTO } | { error: { status: number; data: string } };
type UpdateVehicleArg = { vin: string; vehicle: UpdateVehicleTO };

const updateVehicleQueryFn = async ({
    vin: vehicleVin,
    vehicle,
}: UpdateVehicleArg): Promise<UpdateVehicleQueryResult> => {
    try {
        const { data } = await vehiclesApi.updateVehicleByVin(vehicleVin, vehicle, {
            headers: { 'x-correlation-id': uuid() },
        });
        return { data };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        const handledError = errorHandler('Unexpected error occurred while updating the vehicle', loggableError);

        if (isAxiosError(error) && error.response?.status) {
            return {
                error: {
                    status: error.response.status,
                    data: handledError.message,
                },
            };
        }

        throw handledError;
    }
};

const deleteVehicleDeviationsByVinV2MutationFn = async ({
    vin,
}: DeleteVehicleDeviationsInput): Promise<{ data: VehicleFE }> => {
    try {
        const { data } = await vehiclesApi.clearDeviationsByVin(vin, correlationHeaders());
        return { data: vehicleFromRestV2(data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler(
            `Unexpected error occurred while requesting the delete deviations for vin: ${vin}`,
            loggableError
        );
    }
};

export const vehiclesV2Api = createApi({
    reducerPath: 'vehiclesV2',
    baseQuery: graphqlRequestBaseQuery({ url: '/graphql' }),
    tagTypes: ['VehicleSummary', 'Vehicle'],
    endpoints: builder => ({
        listVehiclesV2: builder.query<GenericRecord<VehicleFE>, OnUpdateVehicleV2SubscriptionVariables>({
            queryFn: listVehiclesV2QueryFn,
        }),
        /*GraphQL carrier for the live map: the REST `listVehiclesV2` entry above owns the fetched
         list (domain `VehicleFE`), this entry owns the `onUpdateVehicleV2` subscription and
         cross-patches each live `VehicleV2` update into that REST cache via `applyVehicleLiveUpdate`.
         Mirrors the `getVehicleByVin`/`getVehicleByVINV2` split and keeps subscriptions on GraphQL. */
        listVehiclesV2Live: builder.query<null, OnUpdateVehicleV2SubscriptionVariables>({
            queryFn: () => ({ data: null }),
            onCacheEntryAdded: async (arg, { cacheDataLoaded, cacheEntryRemoved, dispatch }) => {
                await cacheDataLoaded;
                const connection = client.graphql({ query: onUpdateVehicleV2 }).subscribe({
                    next: ({ data }) => {
                        if (!data.onUpdateVehicleV2) {
                            console.error(
                                '[listVehiclesV2LiveSubscription]: Please make sure all the attributes are returned in the mutation'
                            );
                            return;
                        }
                        const live = data.onUpdateVehicleV2;
                        dispatch(
                            vehiclesV2Api.util.updateQueryData('listVehiclesV2', arg, draft => {
                                const existing = draft[live.vin];
                                if (existing) {
                                    applyVehicleLiveUpdate(existing, live);
                                }
                            })
                        );
                    },
                    error: error => console.warn(error),
                });
                await cacheEntryRemoved;
                connection.unsubscribe();
            },
        }),
        summaryVehiclesV2: builder.query<VehicleSummaryFE[], void>({
            queryFn: listVehiclesSummaryQueryFn,
            providesTags: ['VehicleSummary'],
        }),
        createVehicle: builder.mutation<VehicleTO, CreateVehicleTO>({
            queryFn: createVehicleQueryFn,
            invalidatesTags: ['VehicleSummary'],
        }),
        updateVehicle: builder.mutation<VehicleTO, UpdateVehicleArg>({
            queryFn: updateVehicleQueryFn,
            invalidatesTags: (_result, _error, { vin }) => ['VehicleSummary', { type: 'Vehicle', id: vin }],
        }),
        getVehicleByVINV2: builder.query<VehicleV2, string>({
            queryFn: getVehicleByVINV2QueryFn,
            onCacheEntryAdded: async (vin, { cacheDataLoaded, cacheEntryRemoved, updateCachedData, dispatch }) => {
                await cacheDataLoaded;
                const connection = client.graphql({ query: onUpdateVehicleByVINV2, variables: { vin } }).subscribe({
                    next: ({ data }) => {
                        if (!data.onUpdateVehicleByVINV2) {
                            console.error(
                                '[getVehicleByVINV2Subscription]: Please make sure all the attributes are returned in the mutation'
                            );
                            return;
                        }
                        const vehicle = parseVehicle(data.onUpdateVehicleByVINV2);
                        updateCachedData(() => vehicle);
                        /*Intentionally patches `getVehicleByVin` (REST, domain `VehicleFE`)
                         this endpoint's own `getVehicleByVINV2` cache (AppSync, `VehicleV2`).
                         Both are "v2" of different APIs. */
                        dispatch(
                            vehiclesV2Api.util.updateQueryData('getVehicleByVin', vin, draft => {
                                applyVehicleLiveUpdate(draft, vehicle);
                            })
                        );
                    },
                    error: error => console.warn(error),
                });
                await cacheEntryRemoved;
                connection.unsubscribe();
            },
        }),
        getVehicleByVin: builder.query<VehicleFE, string>({
            queryFn: getVehicleByVinQueryFn,
            providesTags: (_result, _error, vin) => [{ type: 'Vehicle', id: vin }],
        }),
        listVehiclesByFleetIdV2: builder.query<GenericRecord<VehicleV2>, string>({
            queryFn: listVehiclesByFleetIdV2QueryFn,
        }),
        deleteVehicleDeviationsByVinV2: builder.mutation<VehicleFE, DeleteVehicleDeviationsInput>({
            queryFn: deleteVehicleDeviationsByVinV2MutationFn,
        }),
    }),
});

export const {
    useListVehiclesV2Query,
    useLazyListVehiclesV2Query,
    useListVehiclesV2LiveQuery,
    useGetVehicleByVINV2Query,
    useLazyGetVehicleByVINV2Query,
    useSummaryVehiclesV2Query,
    useLazySummaryVehiclesV2Query,
    useCreateVehicleMutation,
    useUpdateVehicleMutation,
    useGetVehicleByVinQuery,
    useLazyGetVehicleByVinQuery,
    useListVehiclesByFleetIdV2Query,
    useLazyListVehiclesByFleetIdV2Query,
    useDeleteVehicleDeviationsByVinV2Mutation,
} = vehiclesV2Api;
